import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { theme } from 'styles/theme';

/**
 * 헤더 우클릭 — 열 숨김/표시 팝업
 */
const GridColumnVisibilityPopup = ({
  x,
  y,
  columns = [], // { id, label, visible, locked? }
  onToggle,
  onShowAll,
  onClose,
  freezeToColumnId = null,
  onFreezeToColumn,
  onClearColumnFreeze,
  freezeRowCount = 0,
  onClearRowFreeze,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let left = x;
    let top = y;
    if (left + rect.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - rect.width - 8);
    if (top + rect.height > window.innerHeight - 8) top = Math.max(8, window.innerHeight - rect.height - 8);
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }, [x, y, columns]);

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-label="열 표시"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 10050,
        width: 260,
        maxHeight: 420,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: theme.borderRadius?.sm || 4,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        display: 'flex',
        flexDirection: 'column',
        fontSize: theme.typography.fontSize.sm,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div style={{ padding: '8px 12px', fontWeight: 700, borderBottom: `1px solid ${theme.colors.border}` }}>
        열 숨김 / 표시
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0', minHeight: 80 }}>
        {columns.map((col) => (
          <label
            key={col.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 12px',
              cursor: col.locked ? 'default' : 'pointer',
              opacity: col.locked ? 0.5 : 1,
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={col.visible}
              disabled={col.locked}
              onChange={() => !col.locked && onToggle?.(col.id)}
            />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.label}</span>
          </label>
        ))}
      </div>
      <div style={{ borderTop: `1px solid ${theme.colors.border}`, padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <button type="button" onClick={onShowAll} style={linkBtn}>
          모든 열 표시
        </button>
        {freezeToColumnId ? (
          <button type="button" onClick={onClearColumnFreeze} style={linkBtn}>
            열 고정 해제
          </button>
        ) : null}
        {freezeRowCount > 0 ? (
          <button type="button" onClick={onClearRowFreeze} style={linkBtn}>
            행 고정 해제 ({freezeRowCount}행)
          </button>
        ) : null}
        <button type="button" onClick={onClose} style={{ ...linkBtn, color: theme.colors.text.secondary }}>
          닫기
        </button>
      </div>
    </div>,
    document.body
  );
};

/** 헤더 우클릭 — 해당 열까지 고정 등 간단 메뉴 */
export const GridHeaderContextMenu = ({
  x,
  y,
  columnId,
  columnLabel,
  onOpenVisibility,
  onFreezeToHere,
  onClearColumnFreeze,
  onFreezeRowsToHere,
  onClearRowFreeze,
  freezeToColumnId,
  freezeRowCount,
  canFreezeRows,
  enableUserMerge = false,
  enablePivot = false,
  onOpenMerge,
  onOpenPivot,
  onClose,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const items = [
    { id: 'visibility', label: '열 숨김/표시…' },
    ...(enableUserMerge ? [{ id: 'merge', label: '동일 값 병합…' }] : []),
    ...(enablePivot ? [{ id: 'pivot', label: '피벗…' }] : []),
    { id: 'sep1', separator: true },
    {
      id: 'freezeCol',
      label: columnId === 'mod' || columnId === 'rowNumber' ? '열 고정' : `"${columnLabel}"까지 열 고정`,
      disabled: columnId === 'mod' || columnId === 'rowNumber',
    },
    {
      id: 'unfreezeCol',
      label: '열 고정 해제',
      disabled: !freezeToColumnId,
    },
    { id: 'sep2', separator: true },
    {
      id: 'freezeRow',
      label: '선택 행까지 행 고정',
      disabled: !canFreezeRows,
    },
    {
      id: 'unfreezeRow',
      label: freezeRowCount > 0 ? `행 고정 해제 (${freezeRowCount}행)` : '행 고정 해제',
      disabled: !freezeRowCount,
    },
  ];

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 10050,
        minWidth: 200,
        background: theme.colors.white,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 4,
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        padding: '4px 0',
        fontSize: theme.typography.fontSize.sm,
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, idx) =>
        item.separator ? (
          <div key={`s-${idx}`} style={{ height: 1, margin: '4px 0', background: theme.colors.border }} />
        ) : (
          <button
            key={item.id}
            type="button"
            disabled={item.disabled}
            onClick={() => {
              if (item.disabled) return;
              if (item.id === 'visibility') onOpenVisibility?.();
              if (item.id === 'merge') onOpenMerge?.();
              if (item.id === 'pivot') onOpenPivot?.();
              if (item.id === 'freezeCol') onFreezeToHere?.();
              if (item.id === 'unfreezeCol') onClearColumnFreeze?.();
              if (item.id === 'freezeRow') onFreezeRowsToHere?.();
              if (item.id === 'unfreezeRow') onClearRowFreeze?.();
              onClose?.();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '6px 12px',
              border: 'none',
              background: 'transparent',
              cursor: item.disabled ? 'default' : 'pointer',
              color: item.disabled ? theme.colors.text.tertiary : theme.colors.text.primary,
              fontSize: 'inherit',
            }}
            onMouseEnter={(e) => {
              if (!item.disabled) e.currentTarget.style.background = theme.colors.primaryLight;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {item.label}
          </button>
        )
      )}
    </div>,
    document.body
  );
};

const linkBtn = {
  border: 'none',
  background: 'none',
  color: theme.colors.primary,
  cursor: 'pointer',
  padding: '4px 4px',
  textAlign: 'left',
  fontSize: theme.typography.fontSize.sm,
};

export default GridColumnVisibilityPopup;
